import { useEffect, useState } from 'react';
import type { Platform } from '../contexts/platform';

export interface UseMarkdownResult {
  content: string;
  loading: boolean;
  error: string | null;
}

export interface UseMarkdownOptions {
  filename: string;
  platform?: Platform | null;
}

export const useMarkdown = (
  options: UseMarkdownOptions | string
): UseMarkdownResult => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Support both string and options format for backward compatibility
  const filename = typeof options === 'string' ? options : options.filename;
  const platform = typeof options === 'string' ? null : options.platform;

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        setLoading(true);
        setError(null);

        // Determine the actual filename based on platform
        let actualFilename = filename;
        if (platform && filename) {
          // If platform is specified and filename doesn't already include platform suffix
          if (!filename.includes('-ios') && !filename.includes('-android')) {
            const extension = filename.split('.').pop();
            const baseName = filename.replace(`.${extension}`, '');
            actualFilename = `${baseName}-${platform}.${extension}`;
          }
        }

        // Fetch the markdown content directly from the content directory
        const response = await fetch(`./content/${actualFilename}`);

        if (!response.ok) {
          throw new Error(
            `Failed to load ${actualFilename}: ${response.statusText}`
          );
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load content');
        setContent('');
      } finally {
        setLoading(false);
      }
    };

    if (filename) {
      loadMarkdown();
    }
  }, [filename, platform]);

  return { content, loading, error };
};
